import { useEffect, useId, type ReactNode, type RefObject } from 'react';
import { create } from 'zustand';

/**
 * PageChrome (PRD §4.3 / §6.3, T-30): a page registers its compact title and
 * its ONE primary action; the TopBar shows them once the page header has
 * scrolled under the bar. The TopBar button runs the page's own handler with
 * the page's own disabled / busy state (the page passes the same values it
 * renders in its header), so no logic is duplicated.
 *
 *   const headerRef = useRef<HTMLElement>(null);
 *   usePageChrome({ title: ticket.title, anchorRef: headerRef, action: primaryDef });
 *
 * Pages with early returns (loading / not found) render `<PageChrome … />`
 * after them instead of calling the hook (same effect, no hook-order issue).
 */
export interface PageChromeAction {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  /** Extra content after the label (e.g. the running time on "עצירה ורישום"). */
  extra?: ReactNode;
  disabled?: boolean;
  busy?: boolean;
}

export interface PageChromeOptions {
  /** Compact title; defaults to the text of the page's h1. */
  title?: string | null;
  action?: PageChromeAction | null;
  /** The page header: chrome appears once its bottom passes under the TopBar. Defaults to `#main h1`. */
  anchorRef?: RefObject<HTMLElement | null>;
}

interface PageChromeEntry {
  owner: string;
  title: string | null;
  action: PageChromeAction | null;
  anchor: HTMLElement | null;
}

interface PageChromeStore {
  entry: PageChromeEntry | null;
  set: (entry: PageChromeEntry) => void;
  clear: (owner: string) => void;
}

export const usePageChromeStore = create<PageChromeStore>((set) => ({
  entry: null,
  set: (entry) => set({ entry }),
  // Only the page that registered may clear (a new page may already own it).
  clear: (owner) => set((s) => (s.entry?.owner === owner ? { entry: null } : s)),
}));

/** Register the page's compact title and primary action for the TopBar. */
export function usePageChrome({ title = null, action = null, anchorRef }: PageChromeOptions): void {
  const owner = useId();
  const set = usePageChromeStore((s) => s.set);
  const clear = usePageChromeStore((s) => s.clear);

  // Re-registered on every render on purpose: the action's handler and its
  // disabled / busy state must always be the ones the header button has.
  useEffect(() => {
    set({ owner, title, action, anchor: anchorRef?.current ?? null });
  });
  useEffect(() => () => clear(owner), [owner, clear]);
}

/** Component form of `usePageChrome`, for pages that return early before their header exists. */
export function PageChrome(props: PageChromeOptions): null {
  usePageChrome(props);
  return null;
}

/**
 * Pure: is the page header out of view? True once its bottom edge has passed
 * under the TopBar's bottom edge (a header that was never measured counts as
 * in view, so nothing appears on a page without one).
 */
export function headerScrolledAway(headerBottom: number | null | undefined, chromeBottom: number): boolean {
  return typeof headerBottom === 'number' && headerBottom < chromeBottom;
}

/** Pure: the compact title to show (the page's own wins over the h1 text). */
export function compactTitle(registered: string | null | undefined, h1Text: string | null | undefined): string | null {
  const own = registered?.trim();
  if (own) return own;
  const fromH1 = h1Text?.trim();
  return fromH1 || null;
}
